import { React, useState, useEffect } from 'react'
import { useWatch, useForm, Controller, useFieldArray } from "react-hook-form";
import fetchMethodRequest from '../../../../config/service';
import { Dialog } from "primereact/dialog";
// import { markRequired } from '../../../Form/CommonFormFunctions';
import { Button } from 'primereact/button';
import { Modal, ModalHeader, ModalBody, ModalFooter, Card, CardBody, Badge } from "reactstrap";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import showToasterMessage from '../../../UI/ToasterMessage/toasterMessage';
import DeleteRowModal from '../../../Cruds/CommonModals/DeleteRowModal.jsx'
import Loader from '../../../App/Loader.jsx';


// ─── helpers ─────────────────────────────────────────────────────────────────

const EMPTY_PENDING_ROW = () => ({
    label: '',
    name: '',
    type: null,
    sortable: false,
    filter: false,
    show: true,
    errors: {},          // per-row validation errors
});

const toCamelCase = (str) =>
    str
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .split(' ')
        .filter(Boolean)
        .map((word, i) =>
            i === 0
                ? word.toLowerCase()
                : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join('');

// ─── component ────────────────────────────────────────────────────────────────

const DocumentUploadConfigs = () => {
    const [fieldsMap, setFieldsMap] = useState({});
    const [showFieldDialog, setShowFieldDialog] = useState(false);
    const [activeRowIndex, setActiveRowIndex] = useState(null);
    const [dialogMode, setDialogMode] = useState("add"); // "add" | "edit"

    // ── "add" mode: list of pending rows to be saved together ──
    const [pendingFields, setPendingFields] = useState([EMPTY_PENDING_ROW()]);

    // ── "edit" mode: inline-editing state ──
    const [editingFieldIndex, setEditingFieldIndex] = useState(null);
    const [editRow, setEditRow] = useState(EMPTY_PENDING_ROW());
    const [editErrors, setEditErrors] = useState({});
    const [isLoading, setisLoading] = useState(false);

    const [deleteModalData, setDeleteModalData] = useState({ isOpen: false, index: null });

    const fieldTypeOptions = [
        { label: "String", value: "string" },
        { label: "Number", value: "number" },
        { label: "Date", value: "date" },
        { label: "Email", value: "email" },
        { label: "Boolean", value: "boolean" }
    ];

    const responseData = {
        "documentUploadConfigs": [
            {
                "name": "invoice",
                "keyWords": "invoice, bill, receipt",
                "fields": [
                    { "name": "amount", "type": "string" },
                    { "name": "filepath", "type": "string" }
                ],
                "errorValidations": "Invalid date formats"
            },
            {
                "name": "aggrements",
                "keyWords": "agreement, contract",
                "fields": [
                    { "name": "name", "type": "string" },
                    { "name": "created", "type": "date" }
                ],
                "errorValidations": "Missing Date"
            }
        ]
    };

    const {
        handleSubmit: handleDocumentUploadSubmit,
        getValues: getDocumntUploadValues,
        trigger: triggerDocumentUploads,
        reset: resetDocumentUploads,
        clearErrors: clearDocumentUploadErrors,
        formState: { errors: documentErrors },
        control: documentUploadControl,
    } = useForm({
        defaultValues: { documentUploadConfigs: [] }
    });

    const {
        fields: uploadConfigFields,
        append: appendDocumentUploadConfigs,
        remove: removeDocumentUploadConfigs,
    } = useFieldArray({
        control: documentUploadControl,
        name: "documentUploadConfigs",
    });

    useEffect(() => { getScreensData(); }, []);

    const getScreensData = async () => {
        setisLoading(true);
        const url = `dynamicScreens`;
        const resp = await fetchMethodRequest('GET', url);
        if (resp && resp.dynamicScreens) {
            const documentUploadConfigs = resp?.dynamicScreens[0]?.smartBuilderScreens || []
            resetDocumentUploads({ documentUploadConfigs: documentUploadConfigs });
            const initialFieldsMap = {};
            documentUploadConfigs.forEach((config, index) => {
                initialFieldsMap[index] = (config.fields || []).map(f => ({
                    name: f.name,
                    type: f.type,
                    label: f.label || "",
                    sortable: f.sortable || false,
                    filter: f.filter || false,
                    show: f.show ?? true
                }));
            });
            setFieldsMap(initialFieldsMap);
        }
        setisLoading(false);
    }

    const handleAddUploadConfigOptions = async () => {
        const isValid = await triggerDocumentUploads("documentUploadConfigs");
        if (!isValid) return;
        appendDocumentUploadConfigs({ name: '', keyWords: '', fields: '', errorValidations: '' });
    };

    const clearFieldErrorOnChange = (name, onChange) => (e) => {
        onChange(e);
    };

    // ── open dialog ────────────────────────────────────────────────────────────

    const openAddFieldDialog = (index, type) => {
        setActiveRowIndex(index);
        setDialogMode(type);
        setEditingFieldIndex(null);
        setEditRow(EMPTY_PENDING_ROW());
        setEditErrors({});
        if (type === 'add') {
            setPendingFields([EMPTY_PENDING_ROW()]);
        }
        setShowFieldDialog(true);
    };

    const closeDialog = () => {
        setShowFieldDialog(false);
        setEditingFieldIndex(null);
        setPendingFields([EMPTY_PENDING_ROW()]);
        setEditRow(EMPTY_PENDING_ROW());
        setEditErrors({});
    };

    // ── pending-field helpers (add mode) ───────────────────────────────────────

    const updatePendingRow = (rowIdx, patch) => {
        setPendingFields(prev => {
            const next = [...prev];
            next[rowIdx] = { ...next[rowIdx], ...patch };

            // clear per-field errors on edit
            if (patch.name !== undefined && next[rowIdx].errors?.name) {
                next[rowIdx].errors = { ...next[rowIdx].errors, name: '' };
            }
            if (patch.type !== undefined && next[rowIdx].errors?.type) {
                next[rowIdx].errors = { ...next[rowIdx].errors, type: '' };
            }
            return next;
        });
    };

    const addPendingRow = () => {
        setPendingFields(prev => [...prev, EMPTY_PENDING_ROW()]);
    };

    const removePendingRow = (rowIdx) => {
        setPendingFields(prev => prev.filter((_, i) => i !== rowIdx));
    };

    const validatePendingFields = () => {
        let valid = true;
        const validated = pendingFields.map(row => {
            const errors = {};
            if (!row.name?.trim()) { errors.name = "Name is required"; valid = false; }
            if (!row.type) { errors.type = "Type is required"; valid = false; }
            return { ...row, errors };
        });
        setPendingFields(validated);
        return valid;
    };

    const saveAllPendingFields = () => {
        if (!validatePendingFields()) return;
        const newFields = pendingFields.map(({ errors, ...rest }) => rest);
        setFieldsMap(prev => ({
            ...prev,
            [activeRowIndex]: [...(prev[activeRowIndex] || []), ...newFields]
        }));
        closeDialog();
    };

    // ── edit-mode helpers ──────────────────────────────────────────────────────

    const startEditingField = (fieldIndex) => {
        const f = fieldsMap[activeRowIndex][fieldIndex];
        setEditingFieldIndex(fieldIndex);
        setEditRow({ ...f, errors: {} });
        setEditErrors({});
    };

    const updateEditRow = (patch) => {
        setEditRow(prev => ({ ...prev, ...patch }));
        if (patch.name !== undefined && editErrors.name) setEditErrors(p => ({ ...p, name: '' }));
        if (patch.type !== undefined && editErrors.type) setEditErrors(p => ({ ...p, type: '' }));
    };

    const saveEditedField = () => {
        const errs = {};
        if (!editRow.name?.trim()) errs.name = "Name is required";
        if (!editRow.type) errs.type = "Type is required";
        if (Object.keys(errs).length) { setEditErrors(errs); return; }

        setFieldsMap(prev => {
            const existing = [...(prev[activeRowIndex] || [])];
            const { errors, ...clean } = editRow;
            existing[editingFieldIndex] = clean;
            return { ...prev, [activeRowIndex]: existing };
        });
        setEditingFieldIndex(null);
        setEditRow(EMPTY_PENDING_ROW());
        setEditErrors({});
    };

    const deleteField = (fieldIndex) => {
        setFieldsMap(prev => {
            const existing = [...(prev[activeRowIndex] || [])];
            existing.splice(fieldIndex, 1);
            return { ...prev, [activeRowIndex]: existing };
        });
        if (editingFieldIndex === fieldIndex) {
            setEditingFieldIndex(null);
            setEditRow(EMPTY_PENDING_ROW());
        }
    };

    // ── config-row delete ──────────────────────────────────────────────────────

    const getDeleteRowModal = () => (
        <DeleteRowModal
            openDeleteModal={deleteModalData.isOpen}
            closeDeleteModal={closeDeleteModal}
            selectActions={deleteModalData.rowData}
            deleteSelectedRow={deleteSelectedRow}
        />
    );

    const closeDeleteModal = () => setDeleteModalData({ isOpen: false, index: null });

    const rebuildFieldsMap = (removedIndex) => {
        const newMap = {};
        Object.keys(fieldsMap).forEach((key) => {
            const k = Number(key);
            if (k < removedIndex) newMap[k] = fieldsMap[k];
            if (k > removedIndex) newMap[k - 1] = fieldsMap[k];
        });
        return newMap;
    };

    const deleteSelectedRow = async () => {
        if (deleteModalData.index !== null) {
            const index = deleteModalData.index;
            const updatedFieldsMap = rebuildFieldsMap(index);
            setFieldsMap(updatedFieldsMap);
            removeDocumentUploadConfigs(index);
            const updatedValues = getDocumntUploadValues();
            const cleaned = {
                ...updatedValues,
                documentUploadConfigs: (updatedValues.documentUploadConfigs || []).map((opt, i) => ({
                    ...opt,
                    name: opt.name.trim(),
                    fields: updatedFieldsMap[i] || []
                }))
            };
            await sendDataToServer(cleaned);
        }
        closeDeleteModal();
    };

    // ── server ─────────────────────────────────────────────────────────────────

    const sendDataToServer = async (data) => {
        let payload = {};
        setisLoading(true);
        payload.smartBuilderScreens = data.documentUploadConfigs ?? null;
        console.log('dynamicScreens' , payload);
        const resp = await fetchMethodRequest('POST', `dynamicScreens`, payload);
        if (resp?.respCode) {
            window.dispatchEvent(new Event("refreshMenu"));
            showToasterMessage("Updated Successfully", "success");
        }
        setisLoading(false);
    };

    const onSubmitForDocumentUploadConfig = async (formData) => {
        const cleaned = {
            ...formData,
            documentUploadConfigs: (formData.documentUploadConfigs || []).map((opt, index) => ({
                ...opt,
                name: opt.name.trim(),
                fields: (fieldsMap[index] || []).map(f => ({
                    name: f.name,
                    type: f.type,
                    label: f.label,
                    sortable: f.sortable,
                    filter: f.filter,
                    show: f.show
                }))
            }))
        };
        sendDataToServer(cleaned);
    };

    // ── sub-components ─────────────────────────────────────────────────────────

    const SettingsCard = ({ title, className = '', toggle, children, disabled = false }) => (
        <div className={`settings-card ${className}`}>
            <div className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-title">{title}</h3>
                {toggle}
            </div>
            <div className="card-content position-relative">
                {disabled && <div className="card-overlay" />}
                {children}
            </div>
        </div>
    );

    // ── file-upload config rows ────────────────────────────────────────────────

    const fileUpload = () => (
        <div className="mb-5">
            {uploadConfigFields.length > 0 && (
                <div className='row'>
                    <div className='col-md-2'><p>Name</p></div>
                    <div className='col-md-3'><p>Key Words</p></div>
                    <div className='col-md-4'><p>Conditions</p></div>
                    <div className='col-md-2'><p>Fields</p></div>
                </div>
            )}

            <div className="mb-3">
                {uploadConfigFields.map((field, index) => (
                    <div className="row mb-2" key={field.id}>
                        <div className="col-md-2">
                            <Controller
                                name={`documentUploadConfigs.${index}.name`}
                                control={documentUploadControl}
                                rules={{ required: "Name is required" }}
                                render={({ field: f }) => (
                                    <input
                                        {...f}
                                        type="text"
                                        className="p-inputtext p-component p-filled w-100"
                                        style={{ borderColor: documentErrors?.documentUploadConfigs?.[index]?.name ? '#dc3545' : '', height: '38px' }}
                                        onChange={clearFieldErrorOnChange(`documentUploadConfigs.${index}.name`, f.onChange)}
                                    />
                                )}
                            />
                        </div>

                        <div className="col-md-3">
                            <Controller
                                name={`documentUploadConfigs.${index}.keyWords`}
                                control={documentUploadControl}
                                rules={{ required: "Key Words is required" }}
                                render={({ field: f }) => (
                                    <input
                                        {...f}
                                        type="text"
                                        className="p-inputtext p-component p-filled w-100"
                                        style={{ borderColor: documentErrors?.documentUploadConfigs?.[index]?.keyWords ? '#dc3545' : '', height: '38px' }}
                                        onChange={clearFieldErrorOnChange(`documentUploadConfigs.${index}.keyWords`, f.onChange)}
                                    />
                                )}
                            />
                        </div>

                        <div className="col-md-4">
                            <Controller
                                name={`documentUploadConfigs.${index}.errorValidations`}
                                control={documentUploadControl}
                                render={({ field: f }) => (
                                    <input
                                        {...f}
                                        type="text"
                                        className="p-inputtext p-component p-filled w-100"
                                        style={{ height: '38px' }}
                                        onChange={clearFieldErrorOnChange(`documentUploadConfigs.${index}.errorValidations`, f.onChange)}
                                    />
                                )}
                            />
                        </div>

                        <div className="col-md-2 d-flex align-items-start gap-2">
                            <Button
                                icon="pi pi-plus"
                                size="small"
                                style={{ height: '38px' }}
                                className="p-button-rounded p-button-success p-button-outlined"
                                tooltip="Add Fields"
                                tooltipOptions={{ position: 'top' }}
                                onClick={() => openAddFieldDialog(index, 'add')}
                                type="button"
                            />
                            <Button
                                icon="pi pi-pencil"
                                size="small"
                                style={{ height: '38px' }}
                                className="p-button-rounded p-button-info p-button-outlined"
                                tooltip="Edit Fields"
                                tooltipOptions={{ position: 'top' }}
                                onClick={() => openAddFieldDialog(index, 'edit')}
                                type="button"
                                disabled={!(fieldsMap[index]?.length > 0)}
                            />
                            {fieldsMap[index]?.length > 0 && (
                                <span className="badge bg-secondary mt-2" style={{ fontSize: '0.75rem' }}>
                                    {fieldsMap[index].length}
                                </span>
                            )}
                        </div>

                        <div className="col-md-1 d-flex align-items-start justify-content-end">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => setDeleteModalData({ isOpen: true, index })}
                            >
                                ×
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const getDocumentUploadConfigs = () => (
        <div className="settings-row">
            <SettingsCard title="Smart Screen Builder" className="ai-settings">
                <div className="d-flex align-items-center justify-content-between mt-3 mb-3">
                    <h3 className="mb-0">Screen Configurations</h3>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={handleAddUploadConfigOptions}
                        id="mail-config-add-btn"
                    >
                        Add +
                    </button>
                </div>
                {fileUpload()}
            </SettingsCard>
        </div>
    );

    // ── ADD MODE: multi-row pending field editor ───────────────────────────────

    const renderAddModeBody = () => (
        <div className="mt-2">
            {/* Column headers */}
            <div className="row fw-semibold mb-2 px-1 align-items-center">
                <div className="col-md-3">Label</div>
                <div className="col-md-3">Name <span className="text-danger">*</span></div>
                <div className="col-md-2">Type <span className="text-danger">*</span></div>
                <div className="col-md-3">Options</div>
                <div className="col-md-1"></div>
            </div>
            {/* <hr className="mt-0 mb-2" /> */}

            {pendingFields.map((row, rowIdx) => (
                <div key={rowIdx} className="row g-2 align-items-start p-1 border-bottom">
                    {/* Label */}
                    <div className="col-md-3">
                        <InputText
                            value={row.label}
                            onChange={(e) => updatePendingRow(rowIdx, { label: e.target.value })}
                            placeholder="Label"
                            className="w-100"
                            style={{ height: '32px' }}
                        />
                    </div>

                    {/* Name */}
                    <div className="col-md-3">
                        <InputText
                            value={row.name}
                            onChange={(e) => updatePendingRow(rowIdx, { name: e.target.value })}
                            placeholder="Field name"
                            style={{ height: '32px' }}
                            className={`w-100 ${row.errors?.name ? 'p-invalid' : ''}`}
                        />
                        {row.errors?.name && <small className="text-danger">{row.errors.name}</small>}
                    </div>

                    {/* Type */}
                    <div className="col-md-2">
                        <Dropdown
                            value={row.type}
                            options={fieldTypeOptions}
                            onChange={(e) => updatePendingRow(rowIdx, { type: e.value })}
                            placeholder="Type"
                            style={{ height: '32px' }}
                            className={`w-100 ${row.errors?.type ? 'p-invalid' : ''}`}
                        />
                        {row.errors?.type && <small className="text-danger">{row.errors.type}</small>}
                    </div>

                    {/* Checkboxes */}
                    <div className="col-md-3 d-flex gap-2 align-items-center flex-wrap pt-1">
                        <div className="form-check mb-0">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id={`sortable-${rowIdx}`}
                                checked={row.sortable}
                                onChange={(e) => updatePendingRow(rowIdx, { sortable: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor={`sortable-${rowIdx}`}>Sort</label>
                        </div>
                        <div className="form-check mb-0">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id={`filter-${rowIdx}`}
                                checked={row.filter}
                                onChange={(e) => updatePendingRow(rowIdx, { filter: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor={`filter-${rowIdx}`}>Filter</label>
                        </div>
                        <div className="form-check mb-0">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id={`show-${rowIdx}`}
                                checked={row.show}
                                onChange={(e) => updatePendingRow(rowIdx, { show: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor={`show-${rowIdx}`}>Show</label>
                        </div>
                    </div>

                    {/* Remove row */}
                    <div className="col-md-1 m-0 d-flex justify-content-end">
                        <Button
                            icon="pi pi-times"
                            size="small"
                            className="p-button-rounded p-button-danger p-button-text"
                            tooltip="Remove row"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => removePendingRow(rowIdx)}
                            type="button"
                            disabled={pendingFields.length === 1}
                        />
                    </div>
                </div>
            ))}

            {/* Add another row */}
            <div className="mt-2">
                <Button
                    label="Add Another Field"
                    icon="pi pi-plus"
                    className="p-button-outlined p-button-primary p-button-sm"
                    onClick={addPendingRow}
                    type="button"
                />
            </div>
        </div>
    );

    // ── ADD MODE footer ────────────────────────────────────────────────────────

    const renderAddModeFooter = () => (
        <div className="d-flex justify-content-end gap-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-outlined p-button-secondary p-button-sm"
                onClick={closeDialog}
                type="button"
            />
            <Button
                label={`Save All (${pendingFields.length})`}
                icon="pi pi-check"
                className="p-button-primary p-button-sm"
                onClick={saveAllPendingFields}
                type="button"
            />
        </div>
    );

    // ── EDIT MODE ─────────────────────────────────────────────────────────────

    const currentFields = fieldsMap[activeRowIndex] || [];

    const renderEditModeBody = () => (
        <div className="mt-2">
            {currentFields.length === 0 ? (
                <p className="text-muted text-center py-3">No fields added yet.</p>
            ) : (
                <>
                    <div className="row fw-semibold mb-2 px-1">
                        <div className="col-md-4">Name</div>
                        <div className="col-md-4">Type</div>
                        <div className="col-md-4 text-end">Actions</div>
                    </div>
                    <hr className="mt-0 mb-2" />

                    {currentFields.map((f, idx) => (
                        <div key={idx} className="mb-3">
                            {editingFieldIndex === idx ? (
                                <div className="row g-2 align-items-start">
                                    <div className="col-md-3">
                                        <InputText
                                            value={editRow.name}
                                            onChange={(e) => updateEditRow({ name: e.target.value })}
                                            placeholder="Field name"
                                            style={{ height: '34px' }}
                                            className={`w-100 ${editErrors.name ? 'p-invalid' : ''}`}
                                        />
                                        {editErrors.name && <small className="text-danger">{editErrors.name}</small>}
                                    </div>
                                    <div className="col-md-3">
                                        <Dropdown
                                            value={editRow.type}
                                            options={fieldTypeOptions}
                                            onChange={(e) => updateEditRow({ type: e.value })}
                                            placeholder="Select type"
                                            style={{ height: '34px' }}
                                            className={`w-100 ${editErrors.type ? 'p-invalid' : ''}`}
                                        />
                                        {editErrors.type && <small className="text-danger">{editErrors.type}</small>}
                                    </div>
                                    <div className="col-md-3">
                                        <InputText
                                            value={editRow.label}
                                            onChange={(e) => updateEditRow({ label: e.target.value })}
                                            placeholder="Label"
                                            style={{ height: '34px' }}
                                            className="w-100"
                                        />
                                    </div>
                                    <div className="col-md-3 d-flex gap-2 justify-content-end">
                                        <Button icon="pi pi-check" size="small" className="p-button-rounded p-button-success" onClick={saveEditedField} style={{ height: '34px' }} type="button" />
                                        <Button icon="pi pi-times" size="small" className="p-button-rounded p-button-secondary p-button-outlined" onClick={() => { setEditingFieldIndex(null); setEditRow(EMPTY_PENDING_ROW()); setEditErrors({}); }} style={{ height: '34px' }} type="button" />
                                    </div>
                                    <div className="col-md-12 mt-2 d-flex gap-4">
                                        <div className="form-check"><input type="checkbox" checked={editRow.sortable} onChange={(e) => updateEditRow({ sortable: e.target.checked })} /><label className="ms-1">Sortable</label></div>
                                        <div className="form-check"><input type="checkbox" checked={editRow.filter} onChange={(e) => updateEditRow({ filter: e.target.checked })} /><label className="ms-1">Filter</label></div>
                                        <div className="form-check"><input type="checkbox" checked={editRow.show} onChange={(e) => updateEditRow({ show: e.target.checked })} /><label className="ms-1">Show</label></div>
                                    </div>
                                </div>
                            ) : (
                                <div className="row align-items-center">
                                    <div className="col-md-4">
                                        <div><strong>{f.name}</strong></div>
                                        <small className="text-muted">{f.label}</small>
                                    </div>
                                    <div className="col-md-4">
                                        <span className="badge bg-light text-dark border">{f.type}</span>
                                        <div className="mt-1 d-flex gap-2 flex-wrap">
                                            {f.sortable && <span className="badge bg-info">S</span>}
                                            {f.filter && <span className="badge bg-warning">F</span>}
                                            {f.show && <span className="badge bg-success">Show</span>}
                                        </div>
                                    </div>
                                    <div className="col-md-4 d-flex gap-2 justify-content-end">
                                        <Button icon="pi pi-pencil" size="small" className="p-button-rounded p-button-info p-button-outlined" tooltip="Edit" tooltipOptions={{ position: 'top' }} onClick={() => startEditingField(idx)} type="button" />
                                        <Button icon="pi pi-trash" size="small" className="p-button-rounded p-button-danger p-button-outlined" tooltip="Delete" tooltipOptions={{ position: 'top' }} onClick={() => deleteField(idx)} type="button" />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </>
            )}
        </div>
    );

    const renderEditModeFooter = () => (
        <div className="d-flex justify-content-end">
            <Button label="Close" icon="pi pi-times" className="p-button-outlined p-button-secondary" onClick={closeDialog} type="button" />
        </div>
    );

    // ── render ─────────────────────────────────────────────────────────────────

    return (
        <div>
            <Loader loader={isLoading} />
            <form onSubmit={handleDocumentUploadSubmit(onSubmitForDocumentUploadConfig)}>
                {getDocumentUploadConfigs()}
                <div className="d-flex justify-content-end gap-3 me-2 mt-3 mb-3">
                    <Button size="sm" type="submit" value="update">Update</Button>
                </div>
            </form>

            <Dialog
                header={dialogMode === "add" ? "Add Fields" : "Edit Fields"}
                visible={showFieldDialog}
                style={{ width: dialogMode === "add" ? "900px" : "650px" }}
                modal
                onHide={closeDialog}
                footer={dialogMode === "add" ? renderAddModeFooter() : renderEditModeFooter()}
            >
                {dialogMode === "add" ? renderAddModeBody() : renderEditModeBody()}
            </Dialog>

            {getDeleteRowModal()}
        </div>
    );
};

export default DocumentUploadConfigs;